import type { PropsWithChildren } from 'react'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { PublicationCode } from '@/scopes/common/domain/types'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useEnv } from '@/scopes/common/infrastructure/env'
import { QuickSearchBarHeight } from '@/scopes/search/components/QuickSearchBar'
import { getPlpHeroImageUrl } from '@/scopes/search/search/domain/policies/publicationCodes'
import { mqMin, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  width: 100%;
  user-select: none;
  background: ${tok('semantics_views_tinted_background_color')};
  display: flex;
  position: relative;
`

const Content = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: ${zIndex.level3};
  height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_16')});

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_32')});
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: calc(${QuickSearchBarHeight} + ${tokPx('primitives_space_48')});
  }
`

const BackgroundImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: ${tokPx('primitives_area_size_160')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    height: ${tokPx('primitives_area_size_200')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    height: ${tokPx('primitives_area_size_240')};
  }
`

export function Hero({ children }: PropsWithChildren) {
  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL')
  // TODO publicationCode should not be optional, change it when consistent across all sites configurations
  const publicationCode = useConfiguration('publicationCode') as PublicationCode
  const imageUrl = getPlpHeroImageUrl(publicationCode, mediaServerBaseUrl)

  return (
    <Wrapper>
      <BackgroundImage
        src={buildImgSrc(imageUrl, 375)}
        srcSet={buildImgSrcSet(imageUrl, [375, 600, 720, 1440, 1440, 2000, 2400])}
        sizes="100vw"
        alt="" // intentionally empty, only for decoration
        // @ts-expect-error -- Not part of the types yet, but this is an img attribute in modern browsers
        fetchpriority="high"
      />
      <Content>{children}</Content>
    </Wrapper>
  )
}
